import { useEffect, useState } from "react";
import { getProductConfigsPaginatedAndFiltered } from "@utils/api-communication/RestApiCli";
import { ProductConfig } from "types/productManagementTypes";

export interface DeviceConfigs {
  applyConfig: ProductConfig | null;
  otherEntries: ProductConfig[];
  loading: boolean;
  failed: boolean;
}

// The backend matches pid as a case-insensitive regex, so narrow to this device
// the same way rather than dropping entries that differ only in casing.
export const splitDeviceConfigs = (
  pid: string,
  configs: ProductConfig[],
): { applyConfig: ProductConfig | null; otherEntries: ProductConfig[] } => {
  const wanted = pid.toLowerCase();
  const mine = configs.filter((c) => c.pid?.toLowerCase() === wanted);
  return {
    applyConfig: mine.find((c) => c.type === "apply") ?? null,
    otherEntries: mine.filter((c) => c.type !== "apply"),
  };
};

// Uses /product/config, which has long filtered on arbitrary fields. Asking
// /ws/config for a type only works once its backend change ships; until then it
// answers with whichever entry comes first, which may not be the apply one.
export const useDeviceConfigs = (pid: string, reloadKey = 0): DeviceConfigs => {
  const [state, setState] = useState<DeviceConfigs>({
    applyConfig: null,
    otherEntries: [],
    loading: true,
    failed: false,
  });

  useEffect(() => {
    if (!pid) {
      setState({ applyConfig: null, otherEntries: [], loading: false, failed: false });
      return;
    }
    let cancelled = false;
    setState({ applyConfig: null, otherEntries: [], loading: true, failed: false });

    getProductConfigsPaginatedAndFiltered(
      { limit: 50, page: 1, filter: JSON.stringify({ pid }) },
      (response) => {
        if (cancelled) return;
        const body = response?.data ?? {};
        const configs: ProductConfig[] = Array.isArray(body.data) ? body.data : [];
        setState({ ...splitDeviceConfigs(pid, configs), loading: false, failed: false });
      },
      () => {
        if (cancelled) return;
        setState({ applyConfig: null, otherEntries: [], loading: false, failed: true });
      },
    );

    return () => {
      cancelled = true;
    };
  }, [pid, reloadKey]);

  return state;
};

export default useDeviceConfigs;
